
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StatCard } from "@/components/StatCard";
import { TrendingUp, Calendar, CalendarRange } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { usePerformanceData } from "@/hooks/usePerformanceData";

interface PerformanceTilesProps {
  address: string;
}

export function PerformanceTiles({ address }: PerformanceTilesProps) {
  const { data: performanceData, isLoading } = usePerformanceData(address);
  
  // Fetch market data for USD conversion
  const { data: marketData } = useQuery({
    queryKey: ['marketData'],
    queryFn: async () => {
      const { data } = await supabase
        .from('dusk_market_data')
        .select('current_price_usd')
        .order('timestamp', { ascending: false })
        .limit(1)
        .single();
      return data;
    },
  });

  if (isLoading) {
    return <div>Loading performance data...</div>;
  }

  if (!performanceData) {
    return null;
  }

  const duskPrice = marketData?.current_price_usd || 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-medium">Performance Overview</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 md:grid-cols-3">
          <StatCard
            title="Last 24 Hours"
            value={`${Math.floor(performanceData.last24hr.totalRewards)} DUSK`}
            description="Total rewards earned"
            icon={<TrendingUp className="h-4 w-4" />}
            tooltipContent="Total rewards earned in the last 24 hours"
            usdValue={performanceData.last24hr.totalRewards * duskPrice}
          />
          <StatCard
            title="Last 7 Days"
            value={`${Math.floor(performanceData.last7days.totalRewards)} DUSK`}
            description={`Daily avg: ${Math.floor(performanceData.last7days.dailyAverage)} DUSK`}
            icon={<Calendar className="h-4 w-4" />}
            tooltipContent="Total rewards earned in the last 7 days and daily average"
            usdValue={performanceData.last7days.totalRewards * duskPrice}
          />
          <StatCard
            title="Last Month"
            value={`${Math.floor(performanceData.lastMonth.totalRewards)} DUSK`}
            description={`Daily avg: ${Math.floor(performanceData.lastMonth.dailyAverage)} DUSK`}
            icon={<CalendarRange className="h-4 w-4" />}
            tooltipContent="Total rewards earned in the last month and daily average"
            usdValue={performanceData.lastMonth.totalRewards * duskPrice}
          />
        </div>
      </CardContent>
    </Card>
  );
}
